import { Theme } from '@mui/material/styles';
import {
  isPrivateOfferPlanCode,
  SubscriptionPlanCode,
  SubscriptionTier,
} from '../../models/Subscription';

export const tierRank: Record<SubscriptionTier, number> = {
  [SubscriptionTier.BASIC]: 1,
  [SubscriptionTier.MEDIUM]: 2,
  [SubscriptionTier.PRO]: 3,
};

export function isPopularPlanCode(code: SubscriptionPlanCode): boolean {
  if (isPrivateOfferPlanCode(code)) {
    return false;
  }
  return (
    code === SubscriptionPlanCode.SCHOOL_MEDIUM ||
    code === SubscriptionPlanCode.STUDIO_PRO ||
    code === SubscriptionPlanCode.TUTOR_PRO
  );
}

export { isPrivateOfferPlanCode };

export function planCodeToTier(code: SubscriptionPlanCode | null | undefined): SubscriptionTier {
  if (!code) return SubscriptionTier.BASIC;
  if (code.includes('PRO')) return SubscriptionTier.PRO;
  if (code.includes('MEDIUM')) return SubscriptionTier.MEDIUM;
  return SubscriptionTier.BASIC;
}

export function getTierColor(tier: SubscriptionTier, theme: Theme): string {
  switch (tier) {
    case SubscriptionTier.PRO:
      return theme.palette.secondary.main;
    case SubscriptionTier.MEDIUM:
      return theme.palette.warning.main;
    default:
      return theme.palette.primary.main;
  }
}

/** Popular → secondary; School Pro → gold; otherwise tier color. */
export function resolvePlanAccent(
  code: SubscriptionPlanCode,
  tier: SubscriptionTier,
  theme: Theme,
): string {
  if (code === SubscriptionPlanCode.SCHOOL_PRO) {
    return theme.palette.warning.main;
  }
  if (isPopularPlanCode(code)) {
    return theme.palette.secondary.main;
  }
  return getTierColor(tier, theme);
}

/**
 * Distinct border accents when several private-offer cards share the catalogue grid.
 * Single-offer grids keep the secondary accent used for special offers.
 */
export function resolveSpecialOfferAccent(index: number, count: number, theme: Theme): string {
  if (count <= 1) {
    return theme.palette.secondary.main;
  }
  const palette = [
    theme.palette.secondary.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.primary.main,
    theme.palette.secondary.light,
  ];
  return palette[((index % palette.length) + palette.length) % palette.length];
}

export function formatPlanPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatPlanPercent(value: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(value / 100);
}
